import { useCallback, useRef } from 'react';

const STORAGE_PREFIX = 'kuadra_draft_';

/**
 * Hook for auto-saving form drafts to localStorage.
 *
 * Usage:
 *   const draft = useFormDraft('addTransaction');
 *
 *   // On modal open (if not editing):
 *   const saved = draft.load();
 *   if (saved) { setAmount(saved.amount); ... }
 *
 *   // On every field change (via useEffect):
 *   useEffect(() => { draft.save({ amount, category, ... }); }, [amount, category, ...]);
 *
 *   // On successful save:
 *   draft.clear();
 */
export function useFormDraft(key) {
  const timerRef = useRef(null);

  const save = useCallback((data) => {
    // Debounce saves to avoid excessive writes
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(data));
      } catch {
        // localStorage full or unavailable — silently ignore
      }
    }, 300);
  }, [key]);

  const load = useCallback(() => {
    try {
      const raw = localStorage.getItem(STORAGE_PREFIX + key);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }, [key]);

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    try {
      localStorage.removeItem(STORAGE_PREFIX + key);
    } catch {
      // silently ignore
    }
  }, [key]);

  return { save, load, clear };
}
